/**
 * Is this document MSAL's hidden token-renewal frame rather than the app?
 *
 * WHY THIS EXISTS
 * ---------------
 * `redirectUri` is the application root. MSAL's silent token renewal works by
 * creating a hidden iframe, pointing it at the authorize endpoint with
 * `prompt=none`, and letting the authority redirect back to `redirectUri` with
 * the result in the URL fragment. The parent window then reads that fragment off
 * the frame's URL.
 *
 * The frame never needs to run any application code — but because redirectUri IS
 * the app, the browser loads index.html into it and Angular bootstraps A SECOND
 * COMPLETE INSTANCE of the application there: its own MSAL, its own project
 * restore, its own historical backfill, its own polling.
 *
 * That second instance is why the duplicate telemetry load survived every fix
 * aimed at it. Each fix was correct and each was verified in the main window's
 * console, where the counters legitimately read 1 — while the network panel
 * showed 2, because it reports requests from EVERY frame. The duplicate was
 * never in the JS context doing the counting.
 *
 * It is also self-sustaining: the interceptor calls acquireTokenSilent on every
 * data request, each silent renewal opens a frame, and each frame boots an app
 * that issues data requests. The observed count rising on a longer-running tab
 * (2, then 3) is the shape of that, not of a race.
 *
 * IT CANNOT BREAK THE TOKEN HANDOFF, and that is worth stating precisely
 * because the failure mode would be silent logouts.
 *
 * Verified against @azure/msal-browser 4.29.0, SilentHandler.monitorIframeForHash:
 * the PARENT polls `iframe.contentWindow.location` and resolves as soon as that
 * URL is readable and not about:blank, then removes the frame. It never executes
 * or waits for any JavaScript inside the frame. So the handoff needs exactly two
 * things from this document — that it is navigated to redirectUri carrying the
 * response, and that the parent can read its location (same origin). Declining
 * to bootstrap Angular affects neither. There are no postMessage listeners
 * anywhere in this app, which is consistent: MSAL polls, it does not message.
 *
 * The top-window redirect flow is untouched. There the frame check is false, the
 * app boots, and MsalRedirectComponent handles the response as it always has.
 *
 * THE RULE
 * --------
 * A document whose window is not the top window, carrying an auth response, is a
 * token frame. Do not bootstrap into it.
 *
 * Deliberately narrow: it requires BOTH the frame check and an auth response, so
 * a legitimate embedding of this app in an iframe still runs normally. The cost
 * of being too broad is a blank page in a valid context; the cost of being too
 * narrow is a duplicate app.
 *
 * Both the fragment AND the query string are checked. This app leaves
 * serverResponseType at its default of FRAGMENT, so today the response is always
 * in the hash — but MSAL supports QUERY, and a config change there would
 * silently reopen the duplicate. Checking both costs nothing and removes the
 * dependency on a default staying put.
 */
const AUTH_RESPONSE = /[#?&](code|id_token|access_token|error|error_description|state)=/;

export function isAuthTokenFrame(win: Window = window): boolean {
  let framed: boolean;
  try {
    framed = win.self !== win.top;
  } catch {
    // Cross-origin access to `top` throws; being unable to see the top window at
    // all is itself strong evidence of being framed by something else.
    framed = true;
  }
  if (!framed) {
    return false;
  }
  const loc = win.location;
  return (
    AUTH_RESPONSE.test(loc?.hash ?? '') || AUTH_RESPONSE.test(loc?.search ?? '')
  );
}

/**
 * Is this document an MSAL sign-in POPUP that has landed back on redirectUri?
 *
 * The External ID (CIAM) sign-in uses a popup (external-id-sign-in.service.ts). Its redirectUri
 * has to be a registered SPA URI, and on 633c6a40 those are the bare origins only, so the popup
 * lands on the application root, just as the silent-renewal frame does, and would bootstrap a
 * full second app the same way.
 *
 * Worse than the frame case: the handoff is by POLLING here too (verified in msal-browser 4.29.0,
 * PopupClient.monitorPopupForHash, which reads popupWindow.location.hash/search from the opener).
 * A second app booting in the popup runs the workforce MSAL's redirect handling, which can consume
 * and clear that hash before the opener reads it, so sign-in would fail intermittently, by timing.
 *
 * THE RULE: a window with a SAME-ORIGIN opener, carrying an auth response, is a sign-in popup.
 * Same-origin is required because only our own app's MSAL polls it; a cross-origin opener throws
 * on access and is treated as "not ours", so the app boots normally there.
 *
 * KNOWN FALSE POSITIVE: a future window.open of a SAME-ORIGIN app route without noopener, whose tab
 * then does a workforce loginRedirect, would come back with an auth response and a same-origin
 * opener and stay blank. Today every window.open in the app is a blob: PDF. Open app routes with
 * noopener (sme-vericure-webui, f1174a1 review).
 */
export function isAuthPopup(win: Window = window): boolean {
  let opener: Window | null;
  try {
    opener = win.opener as Window | null;
    if (!opener || opener === win) {
      return false;
    }
    // Throws for a cross-origin opener.
    if (opener.location.origin !== win.location.origin) {
      return false;
    }
  } catch {
    return false;
  }
  const loc = win.location;
  return (
    AUTH_RESPONSE.test(loc?.hash ?? '') || AUTH_RESPONSE.test(loc?.search ?? '')
  );
}
